import { format } from 'date-fns';
import { fromISODate } from './dateHelpers';
import type { Menu } from '@/types/api';

export interface CookingDaySummary {
  cookingDate: string;
  menuCount: number;
  hoursNeeded: number;
  catererNames: string[];
}

export interface CookingHelperMessageOptions {
  header?: string;
  footer?: string;
}

const DEFAULT_HEADER = 'Helper confirmed schedule for next week';
const DEFAULT_FOOTER = 'Cleaner please take note of dates for fmb kitchen cleaning';

export function buildCookingDaySummaries(menus: Menu[], hoursPerMenu: number): CookingDaySummary[] {
  const groupedByCookingDate = new Map<string, { menuCount: number; catererNames: Set<string> }>();

  for (const menu of menus) {
    if (menu.type !== 'MENU' || !menu.cookingDate) {
      continue;
    }

    const existing = groupedByCookingDate.get(menu.cookingDate) ?? {
      menuCount: 0,
      catererNames: new Set<string>(),
    };

    existing.menuCount += 1;

    if (menu.assignedCatererNameSnapshot) {
      existing.catererNames.add(menu.assignedCatererNameSnapshot);
    }

    groupedByCookingDate.set(menu.cookingDate, existing);
  }

  return [...groupedByCookingDate.entries()]
    .map(([cookingDate, data]) => ({
      cookingDate,
      menuCount: data.menuCount,
      hoursNeeded: data.menuCount * hoursPerMenu,
      catererNames: [...data.catererNames].sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => a.cookingDate.localeCompare(b.cookingDate));
}

export function formatHours(hours: number): string {
  return Number.isInteger(hours) ? String(hours) : String(Number(hours.toFixed(2)));
}

export function buildCookingHelperMessage(
  summaries: CookingDaySummary[],
  options: CookingHelperMessageOptions
): string {
  const {
    header = DEFAULT_HEADER,
    footer = DEFAULT_FOOTER,
  } = options;

  if (summaries.length === 0) {
    return `${header}\nNo cooking days scheduled for next week.\n${footer}`;
  }

  const lines = summaries.map((summary) => {
    const dateLabel = format(fromISODate(summary.cookingDate), 'd MMMM');
    const hoursLabel = formatHours(summary.hoursNeeded);
    const catererLabel = summary.catererNames.length > 0 ? summary.catererNames.join(', ') : 'Unassigned';

    return `${dateLabel} - ${hoursLabel} hours - Caterer : ${catererLabel}`;
  });

  return [header, ...lines, footer].join('\n');
}

export const cookingHelperMessageDefaults = {
  header: DEFAULT_HEADER,
  footer: DEFAULT_FOOTER,
  hoursPerMenu: 3.5,
};
